﻿<script setup>
import { h, ref, computed, nextTick, onMounted, onUpdated, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import markdownit from 'markdown-it';
import rubyPlugin from 'markdown-it-ruby';
import ContextMenu from '@imengyu/vue3-context-menu';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import NameService from '../../services/NameService';

const md = markdownit({ linkify: true }).disable(['image']).use(rubyPlugin);

md.renderer.rules.heading_open = function (tokens, idx, options, env, self) {
    const token = tokens[idx];
    
    if (token.tag === 'h1') {
        return `<p class="text-xl font-rubik font-bold">`;
    } else if (token.tag === 'h2') {
        return `<p class="text-lg font-rubik font-bold">`;
    } else if (token.tag === 'h3') {
        return `<p class="text-md font-rubik font-bold">`;
    } else if (token.tag === 'h4') {
        return `<p class="text-base font-rubik font-bold">`;
    } else if (token.tag === 'h5') {
        return `<p class="text-sm font-rubik font-bold">`;
    } else if (token.tag === 'h6') {
        return `<p class="text-xs font-rubik font-bold">`;
    }
    return self.renderToken(tokens, idx, options);
};

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    
    if (!token.attrGet('href').startsWith('#')) {
        token.attrPush(['target', '_blank']);
    }
    return self.renderToken(tokens, idx, options);
};

// Props
const props = defineProps({
    staff: {
        type: Object,
        default: () => {},
    },
    position: {
        type: Number,
        default: 0
    },
    category: {
        type: Object,
        default: () => null
    }
});

// Emits
const emit = defineEmits(['add-staff-at', 'paste-staff-at', 'move-staff', 'duplicate-staff', 'delete-staff']);

// Store
const store = useStore();
const { t } = useI18n();

// Refs
const ja = ref(null);
const en = ref(null);
const editRow = ref(null);
const commentTop = ref(0);
const commentLeft = ref(0);
const autocompleteNames = ref([]);
const autoCompleteFocus = ref(null);
const autocompleteTop = ref(0);
const autocompleteLeft = ref(0);
const autocompleteWidth = ref(0);
const autocompleteColumn = ref(null);
const isCommentVisible = ref(false);

// Computed
const staffList = computed(() => store.state.staffList);
const editMode = computed(() => store.state.editMode);

// The view-mode tooltip must not reappear with stale state after switching from edit mode
watch(editMode, () => {
    isCommentVisible.value = false;
});

const hasStaffUrl = computed(() => {
    if (props.staff.isWorkTitle) {
        return false;
    }

    if (store.state.staffPageProvider === 'al') {
        return props.staff.id && props.staff.id > 0;
    }
    // Default to KeyFrame Staff List
    return props.staff.id || props.staff.pn?.ja || props.staff.pn?.en || props.staff.ja || props.staff.en;
});
const staffUrl = computed(() => {
    if (store.state.staffPageProvider === 'al') {
        return `https://anilist.co/${props.staff.isStudio ? 'studio' : 'staff'}/${props.staff.id}`;
    }

    // Default to KeyFrame Staff List
    if (props.staff.id && props.staff.id > 0) {
        return `/${props.staff.isStudio ? 'studio' : 'person'}/${props.staff.id}`;
    }
    if (props.staff.pn?.ja) {
        return `/${props.staff.isStudio ? 'studio' : 'person'}/ja:${encodeURIComponent(props.staff.pn.ja).replace(/\%2F/g, '%252F')}`;
    }
    if (props.staff.pn?.en) {
        return `/${props.staff.isStudio ? 'studio' : 'person'}/en:${encodeURIComponent(props.staff.pn.en).replace(/\%2F/g, '%252F')}`;
    }
    if (props.staff.ja) {
        return `/${props.staff.isStudio ? 'studio' : 'person'}/ja:${encodeURIComponent(props.staff.ja).replace(/\%2F/g, '%252F')}`;
    }
    if (props.staff.en) {
        return `/${props.staff.isStudio ? 'studio' : 'person'}/en:${encodeURIComponent(props.staff.en).replace(/\%2F/g, '%252F')}`;
    }
    return null;
});
const mainName = computed(() => {
    if (!props.staff || !props.staff.pn) return;

    if (typeof props.staff.pn == "object") {
        return [props.staff.pn.ja, props.staff.pn.en].filter(Boolean).join("\t");
    }
    
    return props.staff.pn;
});

const kanjiPlaceholder = computed(() => {
    if (props.staff.isInvisible && props.staff.halfSpace) return t('staffComponent.placeholders.invisibleHalfSpace');
    if (props.staff.isInvisible) return t('staffComponent.placeholders.invisible');
    if (props.staff.halfSpace) return t('staffComponent.placeholders.halfSpace');
    if (props.staff.inQueue) return t('staffComponent.placeholders.translating');
    return t('staffComponent.placeholders.kanji');
});

const romajiPlaceholder = computed(() => {
    if (props.staff.isInvisible && props.staff.halfSpace) return t('staffComponent.placeholders.invisibleHalfSpace');
    if (props.staff.isInvisible) return t('staffComponent.placeholders.invisible');
    if (props.staff.halfSpace) return t('staffComponent.placeholders.halfSpace');
    if (props.staff.inQueue) return t('staffComponent.placeholders.translating');
    return t('staffComponent.placeholders.romaji');
});

const studio = computed(() => {
    if (!props.staff || !props.staff.studio) return;

    if (props.staff.studio && typeof props.staff.studio == "object") {
        return [props.staff.studio.ja, props.staff.studio.en].filter(Boolean).join("\t");
    }
    
    return props.staff.studio;
});

const commentComputed = computed(() => {
    if (!props.staff) return;
    
    return md.render([
        mainName.value ? `**${t('staffComponent.mainName')}:** ${mainName.value}` : null,
        studio.value && editMode.value ? `**${t('staffComponent.studio')}:** ${studio.value}` : null,
        props.staff.comment ? `**${t('staffComponent.comment')}:** ${props.staff.comment}` : null
    ].filter(Boolean).join("\n\n"));
});

const fontMap = {
    'system': 'font-system',
    'monospace': 'font-monospace',
    'sans-serif': 'font-sans-serif',
    'serif': 'font-serif',
    'inter': 'font-inter',
    'rubik': 'font-rubik',
    'noto-sans': 'font-noto-sans',
    'noto-serif': 'font-noto-serif',
    'kiwi-maru': 'font-kiwi-maru',
    'sawarabi-mincho': 'font-sawarabi-mincho'
};

const nativeFontClass = computed(() => {
    return fontMap[store.state.nativeFont] || 'font-inter';
});

const englishFontClass = computed(() => {
    return fontMap[store.state.englishFont] || 'font-inter';
});

const nativeLineHeight = computed(() => {
    // Proportional line height (similar to Tailwind's leading-normal: 1.5)
    return store.state.nativeSize * 1.5;
});

const englishLineHeight = computed(() => {
    // Proportional line height (similar to Tailwind's leading-normal: 1.5)
    return store.state.englishSize * 1.5;
});

// Optimized blacklist check - uses Set for O(1) lookup
const isBlackListed = (id) => {
    if (!id) return false;
    return store.state.idBlacklist.has(id);
};

// Methods
const checkShortcuts = (event) => {
    // Toggle Studio
    if (event.key.toLowerCase() == "s" && !event.shiftKey && !event.ctrlKey && event.altKey && (props.staff.ja || props.staff.en)) {
        toggleStudio();
    }
    // Toggle Work
    if (event.key.toLowerCase() == "w" && !event.shiftKey && !event.ctrlKey && event.altKey && (props.staff.ja || props.staff.en)) {
        toggleWorkTitle();
    }
    // Toggle Work
    if (event.key.toLowerCase() == "l" && !event.shiftKey && !event.ctrlKey && event.altKey && (props.staff.ja || props.staff.en)) {
        toggleSubItem();
    }
    // Toggle Invisibility
    if (event.key.toLowerCase() == "i" && !event.shiftKey && !event.ctrlKey && event.altKey) {
        toggleInvisibility();
    }
    // Toggle NC
    if (event.key.toLowerCase() == "n" && !event.shiftKey && !event.ctrlKey && event.altKey && (props.staff.ja || props.staff.en)) {
        toggleNC();
    }
    // Add main name
    if (event.key.toLowerCase() == "p" && !event.shiftKey && !event.ctrlKey && event.altKey && (props.staff.ja || props.staff.en)) {
        addRealName();
    }
    // Edit comment
    if (event.key.toLowerCase() == "c" && !event.shiftKey && !event.ctrlKey && event.altKey) {
        editComment();
    }
    // Note: the Alt+Shift+V kanji variant toggle shortcut is now handled globally in kanjiVariantToggle.js
    // Move the staff member up or down
    // if (event.altKey && !event.shiftKey && !event.ctrlKey && (event.key == "ArrowDown" || event.key == "ArrowUp")) {
    //     event.preventDefault();

    //     if (event.key == "ArrowUp") {
    //         emit("move-staff", props.staff, props.position - 1);

    //         // New focus
    //         nextTick(() => {
    //             if (!$el.previousElementSibling) {
    //                 return;
    //             }
                
    //             const newInputCol = ja.value === event.target ? 0 : 1;
    //             const newInput = $el.previousElementSibling.querySelectorAll('input[type="text"]')[newInputCol];

    //             if (newInput) {
    //                 newInput.focus();
    //             }
    //         });
    //     } else if (event.key == "ArrowDown") {
    //         emit("move-staff", props.staff, props.position + 1);

    //         // New focus
    //         // does it twice??????
    //         nextTick(() => {
    //             if (!$el.nextElementSibling) {
    //                 return;
    //             }

    //             const newInputCol = ja.value === event.target ? 0 : 1;
    //             const newInput = $el.nextElementSibling.querySelectorAll('input[type="text"]')[newInputCol];

    //             if (newInput) {
    //                 newInput.focus();
    //             }
    //         });
    //     }

    //     return;
    // }

    const isAutocompleting = props.staff && props.staff.isTyping && autocompleteNames.value.length > 0;

    if (isAutocompleting) {
        if (event.key == "Enter" && !event.ctrlKey) {
            if (!autoCompleteFocus.value) {
                autocompleteNames.value = [];
                autoCompleteFocus.value = null;
                return;
            }
            copyAutocompleteStaff(autoCompleteFocus.value, props.staff);
            delete props.staff.isTyping;
        } else if (event.key == "Escape") {
            delete props.staff.isTyping;
            autocompleteNames.value = [];
            autoCompleteFocus.value = null;
        } else if (event.key == "ArrowDown" || event.key == "ArrowUp" || event.key == "Tab") {
            event.preventDefault();

            if (!autoCompleteFocus.value) {
                autoCompleteFocus.value = autocompleteNames.value[0];
                return;
            }

            const index = autocompleteNames.value.indexOf(autoCompleteFocus.value);
            const move = (event.key == "ArrowDown" || (event.key == "Tab" && !event.shiftKey)) ? 1 : -1;

            let newIndex = (index + move) % autocompleteNames.value.length;
            if (newIndex < 0) {
                newIndex = autocompleteNames.value.length - 1;
            }

            autoCompleteFocus.value = autocompleteNames.value[newIndex];
        }
        return;
    }

    if (event.key === "Enter" && event.ctrlKey) {
        emit("add-staff-at", props.position + 1);
    } else if (event.key == "ArrowDown" || event.key == "ArrowUp") {
        const orientation = event.key == "ArrowUp" ? "previousElementSibling" : "nextElementSibling";
        const index = Array.from(event.target.parentNode.children).indexOf(event.target);

        if (event.target.parentNode[orientation] && event.target.parentNode[orientation].children[index]) {
            event.preventDefault();
            event.target.parentNode[orientation].children[index].focus();
        }
    } else if (event.key == "ArrowLeft" || event.key == "ArrowRight") {
        const orientation = event.key == "ArrowLeft" ? "previousElementSibling" : "nextElementSibling";
        const skipRow = event.target.selectionStart == event.target.selectionEnd && (event.key == "ArrowLeft" ? event.target.selectionStart <= 0 : event.target.selectionStart >= event.target.value.length);

        if (skipRow && event.target[orientation] && event.target[orientation].tagName == "INPUT") {
            const newIndex = event.target.selectionStart <= 0 ? event.target[orientation].value.length : 0;

            event.preventDefault();
            event.target[orientation].focus();
            event.target[orientation].selectionStart = event.target[orientation].selectionEnd = newIndex;
        }
    }
};
const prepareAutocomplete = () => {
    autocompleteNames.value = [];
    autoCompleteFocus.value = null;
};
const updateAutocompletePosition = (inputElement, column) => {
    if (!inputElement || !ja.value || !en.value) return;
    
    // Get both input positions
    const jaRect = ja.value.getBoundingClientRect();
    const enRect = en.value.getBoundingClientRect();
    
    // Position under the inputs, spanning both columns
    // Use absolute positioning by adding scroll offsets
    autocompleteTop.value = jaRect.bottom + window.scrollY;
    autocompleteLeft.value = jaRect.left + window.scrollX;
    
    // Calculate combined width of both inputs
    autocompleteWidth.value = enRect.right - jaRect.left;
    
    // Store the active column
    autocompleteColumn.value = column;
};
const typeName = (typing, column) => {
    // Fix Japanese dots
    if (props.staff.ja && /[•·･]/g.test(props.staff.ja)) {
        props.staff.ja = props.staff.ja.replace(/[•·･]/g, "・");
    }

    // Remove any zero-width spaces or line break characters
    if (props.staff.ja && /[\u200B-\u200D\uFEFF\u000A\u000D\u2028\u2029]/g.test(props.staff.ja)) {
        props.staff.ja = props.staff.ja.replace(/[\u200B-\u200D\uFEFF\u000A\u000D\u2028\u2029]/g, "");
    }
    if (props.staff.en && /[\u200B-\u200D\uFEFF\u000A\u000D\u2028\u2029]/g.test(props.staff.en)) {
        props.staff.en = props.staff.en.replace(/[\u200B-\u200D\uFEFF\u000A\u000D\u2028\u2029]/g, "");
    }

    // Greek letters
    if (props.staff.ja && /[Α-Ωα-ωА-Яа-я]/g.test(props.staff.ja)) {
        props.staff.ja = NameService.fixGreekLetters(props.staff.ja);
    }
    if (props.staff.en && /[Α-Ωα-ωА-Яа-я]/g.test(props.staff.en)) {
        props.staff.en = NameService.fixGreekLetters(props.staff.en);
    }

    if (!typing) {
        // Unload the data
        delete props.staff.isTyping;
        return;
    }
    
    // Autocomplete
    autocompleteNames.value = [];
    autoCompleteFocus.value = null;
    props.staff.isTyping = true;

    // Update autocomplete position
    const inputElement = column === 'ja' ? ja.value : en.value;
    updateAutocompletePosition(inputElement, column);

    // Autocomplete only if there is at least 1 character in Japanese or 2 in English
    if ((column == 'ja' && props.staff[column].length > 0) || (column == 'en' && props.staff[column].length > 1)) {
        NameService.findName(column, props.staff).then(names => {
            autocompleteNames.value = names;
            autoCompleteFocus.value = null;
        });
    }
};
const copyAutocompleteStaff = (target) => {
    props.staff.ja = target.ja;
    props.staff.en = target.en;

    if (target.isStudio) props.staff.isStudio = target.isStudio;
    else delete props.staff.isStudio;

    if (target.pn) props.staff.pn = target.pn;
    else delete props.staff.pn;

    if (target.id) props.staff.id = target.id;
    else delete props.staff.id;

    autocompleteNames.value = [];
    autoCompleteFocus.value = null;
};
const findTranslation = () => {
    // Unlink the AniList data
    delete props.staff.id;
    delete props.staff.pn;

    // if JA & en is empty, remove the NC if it exists
    if (props.staff.isNC && (!props.staff.ja && !props.staff.en)) {
        delete props.staff.isNC;
    }

    if (props.staff.ja || props.staff.en) {
        NameService.addToTranslationQueue(props.staff, "lazy", "top", props.category?.name);
    }
};
const updateTranslation = () => {
    if (!props.staff.ja && props.staff.en) {
        // Unlink the AniList data
        delete props.staff.id;
        delete props.staff.pn;

        NameService.addToTranslationQueue(props.staff, "lazy", "top", props.category?.name);
        return;
    }

    // if JA & en is empty, remove the NC if it exists
    if (props.staff.isNC && (!props.staff.ja && !props.staff.en)) {
        delete props.staff.isNC;
    }

    if (props.staff.ja && props.staff.en) {
        NameService.updateStaff(props.staff, {
            en: props.staff.en
        });
    }
};
const formatPaste = async (e) => {
    if (!staffList.value.settings.clipboardFormat) {
        return;
    }

    const text = e.clipboardData.getData("text/plain");
    if (!text || text.length < 4) {
        return;
    }

    const splitRegex = new RegExp(`[${staffList.value.settings.delimiters}]+[ ]*[${staffList.value.settings.delimiters}]*`);
    const lines = NameService.fixGreekLetters(text.trim()).replace(/\n[ 　]*\n/g, "\n\n").split(splitRegex);

    if (lines.length > 1) {
        const allNewStaff = [];

        e.preventDefault();

        for (const line of lines.reverse()) {
            const [ja, en] = line.split(/\t+/g);
            
            allNewStaff.unshift({
                ja: (ja || "").trim(),
                en: (en || "").trim(),
                untranslated: true
            });
        }

        emit("paste-staff-at", props.position + 1, allNewStaff);

        nextTick(() => {
            if (!props.staff.ja && !props.staff.en) {
                emit("delete-staff", props.staff);
            }
        })
    }
};
const showComment = (event) => {
    if (props.staff.comment || props.staff.pn || props.staff.studio) {
        // Only the view-mode row reads isCommentVisible; edit mode uses CSS group-hover instead
        if (!editMode.value) {
            isCommentVisible.value = true;
        }
        let coords;
        
        // In edit mode, position tooltip at the right of the entire row
        if (editMode.value && editRow.value) {
            coords = editRow.value.getBoundingClientRect();
        } else {
            coords = event.currentTarget.getBoundingClientRect();
        }

        // Validate coordinates - if invalid (e.g., during DOM mutations), keep previous position
        if (!coords || coords.width === 0 || coords.height === 0 || (coords.top === 0 && coords.left === 0)) {
            return;
        }

        if ((coords.left * window.devicePixelRatio + coords.width * window.devicePixelRatio + 250) > (window.innerWidth * window.devicePixelRatio)) {
            commentLeft.value = Math.max(0, coords.left - 250);
        } else {
            commentLeft.value = coords.left + coords.width;
        }
        commentTop.value = coords.top;

        // If small screen
        if (window.innerWidth < 800) {
            commentLeft.value = 40;
            commentTop.value = coords.top + coords.height;
        }
    }
};
const isElementInsideComment = (target) => {
    let current = target;

    while (current) {
        if (current.classList && current.classList.contains('md-viewer-comment')) {
            return true;
        }
        current = current.parentElement || (current.nodeType === 3 ? current.parentNode : null);
    }

    return false;
};

const handleRowMouseLeave = (event) => {
    const relatedTarget = event.relatedTarget;

    if (isElementInsideComment(relatedTarget)) {
        return;
    }

    isCommentVisible.value = false;
};

const toggleStudio = () => {
    const isStudio = !props.staff.isStudio;
    if (isStudio && props.staff.isWorkTitle) {
        delete props.staff.isWorkTitle;
    }

    NameService.updateStaff(props.staff, {
        isStudio: isStudio
    });
};
const toggleWorkTitle = () => {
    if (props.staff.isStudio) {
        delete props.staff.isStudio;
    }

    if (props.staff.isWorkTitle) {
        delete props.staff.isWorkTitle;
    } else {
        props.staff.isWorkTitle = true;
    }
}
const toggleSubItem = () => {
    if (props.staff.subItem) {
        delete props.staff.subItem;
    } else {
        props.staff.subItem = true;
    }
};
const toggleNC = () => {
    if (props.staff.isNC) {
        delete props.staff.isNC;
    } else if (props.staff.ja || props.staff.en) { // Needs a name to be NC
        props.staff.isNC = true;
    }
};
const toggleInvisibility = () => {
    if (props.staff.isInvisible) {
        delete props.staff.isInvisible;
    } else {
        props.staff.isInvisible = true;
    }
};
const addRealName = () => {
    let penNameJa, penNameEn;

    if (props.staff.pn) {
        if (typeof props.staff.pn == "object") {
            penNameJa = props.staff.pn.ja;
            penNameEn = props.staff.pn.en;
        } else if (typeof props.staff.pn == "string" && props.staff.pn.includes("\t")) {
            const tabSplit = props.staff.pn.split("\t");

            penNameJa = tabSplit[0];
            penNameEn = tabSplit[1];
        } else if (typeof props.staff.pn == "string" && /^[A-Za-zÀ-ÖØ-öø-ÿĀ-žƀ-ɏḀ-ỿ0-9 .,:;!?()\[\]{}'"’‘“”\/\\_\-°•・･℃@＠#$☆△▲%&*+=^|]+$/g.test(props.staff.pn)) {
            penNameEn = props.staff.pn;
        } else {
            penNameJa = props.staff.pn;
        }
    }

    store.commit('addDualLanguageInput', {
        id: "new-real-name",
        mode: "staff",
        text: t('staffComponent.messages.enterMainName'),
        anilistId: props.staff.id,
        ja: penNameJa,
        en: penNameEn,
        maxlength: 100,
        callback: (ja, en, anilistId) => {
            const initialId = props.staff.id;
            const initialJa = props.staff.ja;
            const initialEn = props.staff.en;

            NameService.updateStaff(props.staff, {
                pn: ja || en ? {
                    ja: ja,
                    en: en
                } : null,
                ...(en && props.staff.ja && !props.staff.en && { en: en }),
                ...(anilistId && anilistId > 0 && { id: anilistId })
            }, (staff) => initialId == staff.id && initialJa == staff.ja && initialEn == staff.en);
        }
    });
};
const addStudioAffiliation = () => {
    let studioJa, studioEn;

    if (props.staff.studio) {
        if (props.staff.studio && typeof props.staff.studio == "object") {
            studioJa = props.staff.studio.ja;
            studioEn = props.staff.studio.en;
        } else if (typeof props.staff.studio == "string" && props.staff.studio.includes("\t")) {
            const tabSplit = props.staff.studio.split("\t");

            studioJa = tabSplit[0];
            studioEn = tabSplit[1];
        } else if (typeof props.staff.studio == "string" && /^[A-Za-zÀ-ÖØ-öø-ÿĀ-žƀ-ɏḀ-ỿ0-9 .,:;!?()\[\]{}'"’‘“”\/\\_\-°•・･℃@＠#$☆△▲%&*+=^|]+$/g.test(props.staff.studio)) {
            studioEn = props.staff.studio;
        } else {
            studioJa = props.staff.studio;
        }
    }

    store.commit('addDualLanguageInput', {
        id: "new-studio-affiliation",
        title: "Studio affiliation",
        mode: "studio",
        text: t('staffComponent.messages.enterStudioAffiliation'),
        anilistId: props.staff?.studio?.id,
        ja: studioJa,
        en: studioEn,
        maxlength: 100,
        callback: (ja, en, anilistId) => {
            if (ja || en) {
                props.staff.studio = {
                    ja: ja,
                    en: en,
                    ...(anilistId && anilistId > 0 && { id: anilistId })
                };
            } else {
                delete props.staff.studio;
            }
        }
    });
};
const editComment = () => {
    store.commit('addPrompt', {
        id: "new-comment",
        text: t('staffComponent.messages.enterComment'),
        type: "textarea",
        maxlength: 1000,
        placeholder: t('staffComponent.comment'),
        value: props.staff.comment,
        callback: (comment) => {
            if (comment) {
                props.staff.comment = comment;
            } else if (comment == "") {
                delete props.staff.comment;
            }
        }
    });
};
const handleClick = (e) => {
    if (!editMode.value) {
        return;
    }

    e.preventDefault();
    e.stopPropagation();
    
    const coords = e.currentTarget.getBoundingClientRect();

    const options = [
        {
            label: t('staffComponent.contextMenu.toggleStudio'),
            hidden: !props.staff.ja && !props.staff.en,
            icon: h(FontAwesomeIcon, {
                class: 'mx-fa-icon',
                icon: 'fa-solid fa-briefcase'
            }),
            onClick: () => {
                toggleStudio();
            }
        },
        {
            label: t('staffComponent.contextMenu.toggleSubItem'),
			hidden: !props.staff.ja && !props.staff.en,
            icon: h(FontAwesomeIcon, {
                class: 'mx-fa-icon',
                icon: 'fa-solid fa-down-left-and-up-right-to-center'
            }),
            onClick: () => {
                toggleSubItem();
            }
        },
        {
            label: t('staffComponent.contextMenu.toggleHalfSpace'),
            hidden: !!(props.staff.ja || props.staff.en),
            icon: h(FontAwesomeIcon, {
                class: 'mx-fa-icon',
                icon: 'fa-solid fa-circle-half-stroke'
            }),
            onClick: () => {
                if (props.staff.halfSpace) {
                    delete props.staff.halfSpace;
                } else {
                    props.staff.halfSpace = true;
                }
            }
        },
        {
            label: t('staffComponent.contextMenu.addStudioAffiliation'),
			hidden: !props.staff.ja && !props.staff.en,
            icon: h(FontAwesomeIcon, {
                class: 'mx-fa-icon',
                icon: 'fa-solid fa-building'
            }),
            onClick: () => {
                addStudioAffiliation();
            }
        },
        {
            label: t('staffComponent.contextMenu.editComment'),
            icon: h(FontAwesomeIcon, {
                class: 'mx-fa-icon',
                icon: 'fa-solid fa-comment'
            }),
            onClick: () => {
                editComment();
            }
        },
        {
            label: t('staffComponent.contextMenu.batchNativeNameCorrection'),
            hidden: !props.staff.ja && !props.staff.en,
            icon: h(FontAwesomeIcon, {
                class: 'mx-fa-icon',
                icon: 'fa-solid fa-arrow-right-arrow-left',
                transform: 'shrink-2'
            }),
            onClick: () => {
                const languageRow = props.staff.ja ? "ja" : "en";
                const language = languageRow === "ja" ? t('staffComponent.languages.japanese') : t('staffComponent.languages.english');
                const name = props.staff[languageRow];
                
                store.commit('addPrompt', {
                    id: "batch-japanese-correction",
                    text: t('staffComponent.messages.enterNewNameFor', { language, name }),
                    type: "input",
                    maxlength: 100,
                    placeholder: props.staff.ja ? t('staffComponent.messages.placeholderJapaneseName') : t('staffComponent.messages.placeholderEnglishName'),
                    value: name,
                    callback: (newName) => {
                        if (!newName) {
                            return;
                        }

                        NameService.updateStaff(props.staff, {
                            [languageRow]: newName
                        });

                        nextTick(function () {
                            NameService.addToTranslationQueue(props.staff, "forced", "top", props.category?.name);
                        });
                    }
                });
            }
        },
        {
            label: t('staffComponent.contextMenu.linkToPage'),
            hidden: !props.staff.ja && !props.staff.en,
            icon: h(FontAwesomeIcon, {
                class: 'mx-fa-icon',
                icon: 'fa-solid fa-link'
            }),
            onClick: () => {
                store.commit('addLinkToPageModal', {
                    id: 'link-to-page-' + Date.now(),
                    initialQuery: props.staff.en || props.staff.ja,
                    callback: (fetchedStaff) => {
                        if (fetchedStaff === -1) {
                            console.log("Unlinking with -1");
                            NameService.updateStaff(props.staff, {
                                id: null,
                                pn: null,
                            });
                            return;
                        }

                        if (!fetchedStaff) return;

                        const jaMatches = (props.staff.ja || '').toLowerCase() === (fetchedStaff.ja || '').toLowerCase();
                        const enMatches = (props.staff.en || '').toLowerCase() === (fetchedStaff.en || '').toLowerCase();
                        let needsPn = !(jaMatches && enMatches);

                        // Ne pas ajouter de PN si le nom japonais correspond, même si le nom anglais ne correspond pas (souvent vide)
                        if (jaMatches && (props.staff.ja || '') !== '') {
                            needsPn = false;
                        }

                        NameService.updateStaff(props.staff, {
                            id: fetchedStaff.id,
                            isStudio: fetchedStaff.isStudio,
                            pn: needsPn ? { ja: fetchedStaff.ja || '', en: fetchedStaff.en || '' } : null,
                            ...(!props.staff.en && props.staff.ja && fetchedStaff.en ? { en: fetchedStaff.en } : {})
                        });
                    }
                });
            }
        },
        {
            label: t('staffComponent.contextMenu.editId'),
            hidden: !props.staff.ja && !props.staff.en,
            icon: h(FontAwesomeIcon, {
                class: 'mx-fa-icon',
                icon: 'fa-solid fa-at'
            }),
            onClick: () => {
                store.commit('addPrompt', {
                    id: "edit-anilist-id",
                    title: "New AniList ID",
                    text: t('staffComponent.messages.enterNewAniListId'),
                    type: "input",
                    maxlength: 15,
                    placeholder: t('staffComponent.messages.placeholderAniListId'),
                    value: props.staff.id,
                    callback: (id) => {
                        if (id && !("" + id).match(/^\d+$/)) {
                            setTimeout(() => {
                                store.commit('addAlert', {
                                    id: "edit-anilist-id-error",
                                    title: "Error",
                                    text: t('staffComponent.messages.invalidAniListId')
                                });
                            });

                            return;
                        }

                        NameService.updateID(props.staff, id);
                    }
                });
            }
        },
        {
            label: t('staffComponent.contextMenu.move'),
            children: [
                {
                    label: t('staffComponent.contextMenu.moveToTop'),
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-angle-up'
                    }),
                    onClick: () => {
                        emit("move-staff", props.staff, 0);
                    }
                },
                {
                    label: t('staffComponent.contextMenu.moveUp'),
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-angle-up'
                    }),
                    onClick: () => {
                        emit("move-staff", props.staff, props.position - 1);
                    }
                },
                {
                    label: t('staffComponent.contextMenu.moveDown'),
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-angle-down'
                    }),
                    onClick: () => {
                        emit("move-staff", props.staff, props.position + 1);
                    }
                },
                {
                    label: t('staffComponent.contextMenu.moveToBottom'),
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-angle-down'
                    }),
                    onClick: () => {
                        emit("move-staff", props.staff, Infinity);
                    }
                },
            ]
        },
        {
            label: t('staffComponent.contextMenu.insert'),
            divided: true,
            children: [
                {
                    label: t('staffComponent.contextMenu.insertStaffAbove'),
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-angle-up'
                    }),
                    onClick: () => {
                        emit("add-staff-at", props.position);
                    }
                },
                {
                    label: t('staffComponent.contextMenu.insertStaffBelow'),
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-angle-down'
                    }),
                    onClick: () => {
                        emit("add-staff-at", props.position + 1);
                    }
                }
            ]
        },
        {
            label: t('staffComponent.contextMenu.more'),
            divided: true,
            children: [
                {
                    label: t('staffComponent.contextMenu.toggleNC'),
                    hidden: !props.staff.ja && !props.staff.en,
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-user-secret'
                    }),
                    onClick: () => {
                        toggleNC();
                    }
                },
                {
                    label: t('staffComponent.contextMenu.toggleInvisibility'),
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-eye'
                    }),
                    onClick: () => {
                        toggleInvisibility();
                    }
                },
                {
                    label: t('staffComponent.contextMenu.toggleWorkTitle'),
                    hidden: !props.staff.ja && !props.staff.en,
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-book'
                    }),
                    onClick: () => {
                        toggleWorkTitle();
                    }
                },
                {
                    label: t('staffComponent.contextMenu.toggleMiscredit'),
                    hidden: !props.staff.ja && !props.staff.en,
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-strikethrough'
                    }),
                    onClick: () => {
                        if (props.staff.isMiscredit) {
                            delete props.staff.isMiscredit;
                        } else {
                            props.staff.isMiscredit = true;
                        }
                    }
                },
                {
                    label: t('staffComponent.contextMenu.addMainName'),
                    hidden: !props.staff.ja && !props.staff.en,
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-user-tie'
                    }),
                    onClick: () => {
                        addRealName();
                    }
                },
                {
                    label: t('staffComponent.contextMenu.disableStaffSync'),
                    divided: true,
                    hidden: !props.staff.ja && !props.staff.en,
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-unlink'
                    }),
                    onClick: () => {
                        props.staff.id = -Date.now()+"";
                    }
                },
                {
                    label: t('staffComponent.contextMenu.searchOnGoogle'),
                    hidden: !props.staff.ja && !props.staff.en,
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-search'
                    }),
                    onClick: () => {
                        window.open(`https://www.google.com/search?q=%22${encodeURIComponent(props.staff.ja || props.staff.en)}%22`, "_blank");
                    }
                },
                {
                    label: t('staffComponent.contextMenu.searchOnKFSL'),
                    hidden: !props.staff.ja && !props.staff.en,
                    icon: h(FontAwesomeIcon, {
                        class: 'mx-fa-icon',
                        icon: 'fa-solid fa-search'
                    }),
                    onClick: () => {
                        window.open(`https://keyframe-staff-list.com/search?advancedSearch=0&q=${encodeURIComponent(props.staff.ja || props.staff.en)}`, "_blank");
                    }
                },
            ]
        },
        {
            label: t('staffComponent.contextMenu.delete'),
            customClass: "important",
            icon: h(FontAwesomeIcon, {
                class: 'mx-fa-icon',
                icon: 'fa-solid fa-trash'
            }),
            onClick: () => {
                emit("delete-staff", props.staff);
            }
        }
    ];

    ContextMenu.showContextMenu({
        theme: store.state.resolvedTheme,
        x: e.x !== 0 ? e.x : coords.x,
        y: e.y !== 0 ? e.y : coords.y + coords.height,
        zIndex: 1000,
        items: options
    });
};
const checkBlackList = () => {
    if (isBlackListed(props.staff.id)) {
        delete props.staff.pn;
        delete props.staff.isNC;
        props.staff.ja = "";
        props.staff.en = "[REDACTED FOR PRIVACY]";
        props.staff.isWorkTitle = true;
    }
}

onMounted(() => {
    if (store.state.loggedIn || !props.staff.id || props.staff.isStudio || editMode.value) {
        return;
    }
    checkBlackList();
})
onUpdated(() => {
    if (store.state.loggedIn || !props.staff.id || props.staff.isStudio || editMode.value) {
        return;
    }
    checkBlackList();
});
</script>

<template>
    <tr v-if="editMode"
        ref="editRow"
        class="group w-full box-border bg-app-even-row odd:bg-app-odd-row"
        :class="{
            'font-bold': staff.isStudio,
            'text-[#777] italic': staff.isWorkTitle,
            'in-queue bg-blue-50 odd:bg-blue-100 dark:bg-blue-950 dark:odd:bg-blue-900': staff.inQueue,
            'show-placeholder': staff.halfSpace,
            'show-placeholder bg-pink-50 odd:bg-pink-100 dark:bg-[#310415] dark:odd:bg-[#250310]': staff.isInvisible,
            'highlighted bg-yellow-200 odd:bg-yellow-200 dark:bg-yellow-800 dark:odd:bg-yellow-700': staff.isHighlighted,
            'blue-highlighted bg-blue-200 odd:bg-blue-200 dark:bg-blue-950 dark:odd:bg-blue-900': staff.isBlueHighlighted,
            'comment-border': staff.comment || staff.pn || staff.studio,
            'shadow-[inset_0_-2px_0_0_var(--tw-shadow-color)] shadow-red-500': (staff.ja || staff.en) && (!staff.isStudio && !staff.isWorkTitle) && ((store.state.autoUnderline === 'id' && !staff.id) || (store.state.autoUnderline === 'idpn' && !staff.pn && !staff.id)),
            'h-8': staff.halfSpace,
        }" @contextmenu="handleClick" @mouseover="store.state.showTooltip === 'both' ? showComment($event) : null">
        <i class="handle p-1 w-1/12 cursor-grab active:cursor-grabbing" @dblclick="$emit('duplicate-staff', staff)">
            <font-awesome-icon icon="fa-solid fa-bars" />
        </i>
        <input ref="ja"
            type="text"
            :placeholder="kanjiPlaceholder"
            v-model.trim="staff.ja"
            class="w-5/12 bg-transparent p-2 text-sm text-center outline-keyframe placeholder-white placeholder-opacity-10"
            :class="[
                {
                    'text-xs': staff.subItem,
                    'line-through': staff.isMiscredit && staff.ja,
                    'h-8 text-xs': staff.halfSpace,
                    'opacity-50': staff.isNC,
                },
                nativeFontClass
            ]"
            :style="{
                ...((!staff.subItem && !staff.halfSpace) && { fontSize: `${store.state.nativeSize}rem`, lineHeight: `${nativeLineHeight}rem` })
            }"
            :readonly="staff.halfSpace || staff.inQueue"
            @keydown="checkShortcuts"
            @change="findTranslation"
            @input="typeName(true, 'ja')"
            @focus="prepareAutocomplete()"
            @focusout="typeName(false, 'ja')"
            @paste="formatPaste"
            @mouseover="store.state.showTooltip === 'both' ? showComment($event) : null"
        />

        <input ref="en"
            type="text"
            :placeholder="romajiPlaceholder"
            v-model.trim="staff.en"
            class="w-5/12 bg-transparent p-2 text-sm text-center outline-keyframe placeholder-white placeholder-opacity-10"
            :class="[
                {
                    'text-xs': staff.subItem,
                    'line-through': staff.isMiscredit && staff.en,
                    'h-8 text-xs': staff.halfSpace,
                    'opacity-50': staff.isNC,
                },
                englishFontClass
            ]"
            :style="{
                ...((!staff.subItem && !staff.halfSpace) && { fontSize: `${store.state.englishSize}rem`, lineHeight: `${englishLineHeight}rem` })
            }"
            :readonly="staff.halfSpace || (staff.inQueue && !staff.ja)"
            @keydown="checkShortcuts"
            @change="updateTranslation"
            @input="typeName(true, 'en')"
            @focus="prepareAutocomplete()"
            @focusout="typeName(false, 'en')"
            @paste="formatPaste"
            @mouseover="store.state.showTooltip === 'both' ? showComment($event) : null"
        />

        <i @click="$emit('delete-staff', staff)" class="p-1 px-[7px] btn-danger text-white text-xs rounded cursor-pointer select-none">
            <font-awesome-icon icon="fa-solid fa-times" />
        </i>
        
        <!-- Comment tooltip in edit mode -->
        <div
            v-if="store.state.showTooltip === 'both' && (staff.comment || staff.pn || staff.studio)"
            ref="comment"
            class="md-viewer-comment pointer-events-auto select-text fixed bg-ocean-400 text-white font-normal text-sm p-2 rounded-md shadow-sm w-[250px] break-words hidden group-hover:block z-40"
            :style="{ top: `${commentTop}px`, left: `${commentLeft}px` }"
            v-html="commentComputed"
            @mouseenter="isCommentVisible = true"
            @mouseleave="isCommentVisible = false"
        ></div>
        
        <!-- Autocomplete (Teleported to body to avoid opacity inheritance) -->
        <Teleport to="body">
            <ul
                v-if="autocompleteNames.length > 0 && staff.isTyping"
                class="block max-h-[400px] absolute rounded-md overflow-hidden overflow-y-auto divide-y divide-white divide-opacity-10 shadow-lg z-50 font-inter text-center"
                :style="{
                    top: `${autocompleteTop}px`,
                    left: `${autocompleteLeft}px`,
                    width: `${autocompleteWidth}px`
                }"
            >
                <li
                    v-for="item in autocompleteNames"
                    :key="`${item.ja}-${item.en}-${item.id}`"
                    @mousedown="copyAutocompleteStaff(item)"
                    @click.prevent.stop="copyAutocompleteStaff(item)"
                    @contextmenu.prevent.stop
                    class="w-full bg-keyframe hover:bg-keyframe-dark text-xs text-white p-2 cursor-pointer font-normal text-center"
                    :class="{'bg-keyframe-darker': item == autoCompleteFocus, 'font-bold': item.isStudio}"
                    tabindex="0"
                >
                    {{ item.ja }} {{ item.en }}
                </li>
            </ul>
        </Teleport>
    </tr>
    <tr v-else
        v-if="!staff.isInvisible"
        class="group relative transform box-border bg-app-even-row odd:bg-app-odd-row"
        :class="{
            'font-bold': staff.isStudio,
            'text-[#777] italic': staff.isWorkTitle,
            'in-queue bg-blue-50 odd:bg-blue-100 dark:bg-blue-950 dark:odd:bg-blue-900': staff.inQueue,
            'highlighted bg-yellow-200 odd:bg-yellow-200 dark:bg-yellow-800 dark:odd:bg-yellow-700': staff.isHighlighted,
            'blue-highlighted bg-blue-200 odd:bg-blue-200 dark:bg-blue-950 dark:odd:bg-blue-900': staff.isBlueHighlighted,
            'h-8': staff.ja == '' && staff.en == '' && !staff.halfSpace,
            'h-4': staff.halfSpace,
            'text-[#999]': staff.isNC,
            'comment-border': staff.comment,
            'shadow-[inset_0_-2px_0_0_var(--tw-shadow-color)] shadow-red-500': (staff.ja || staff.en) && (!staff.isStudio && !staff.isWorkTitle) && ((store.state.autoUnderline === 'id' && !staff.id) || (store.state.autoUnderline === 'idpn' && !staff.pn && !staff.id)),
        }"
        @mouseover="showComment"
        @mouseleave="handleRowMouseLeave"
    >
        <td 
            v-if="staff.ja"
            :class="[
                { 'text-xs': staff.subItem,
                'pb-6': staff.studio,
                'line-through': staff.isMiscredit },
                nativeFontClass
            ]"
            :colspan="staff.en ? 1 : 2"
            :style="{
                ...((!staff.subItem && !staff.halfSpace) && { fontSize: `${store.state.nativeSize}rem`, lineHeight: `${nativeLineHeight}rem` })
            }"
        >
            {{ staff.ja }}
        </td>
        <td 
            v-if="staff.en"
            :class="[
                { 'text-xs': staff.subItem, 'pb-6': staff.studio, 'line-through': staff.isMiscredit },
                englishFontClass
            ]"
            :colspan="staff.ja ? 1 : 2"
            :style="{
                ...((!staff.subItem && !staff.halfSpace) && { fontSize: `${store.state.englishSize}rem`, lineHeight: `${englishLineHeight}rem` })
            }"
        >
            {{ staff.en }}
        </td>
        <td v-if="!staff.ja && !staff.en" 
            :class="[{ 'pb-6': staff.studio }, nativeFontClass]" 
            :style="{
                ...((!staff.subItem && !staff.halfSpace) && { fontSize: `${store.state.nativeSize}rem`, lineHeight: `${nativeLineHeight}rem` })
            }" 
            colspan="2">
        </td>

        <span
            v-if="studio"
            class="w-full px-2 bottom-[0.4rem] left-0 absolute font-bold overflow-hidden text-xs text-ellipsis whitespace-nowrap"
            :title="studio"
        >
            ({{ studio }})
        </span>

        <span v-if="(staff.pn || staff.isNC) && (staff.ja || staff.en)" class="flex absolute top-1 right-1 gap-1 select-none">
            <span v-if="staff.pn" class="bg-ocean-500 text-white px-[4px] py-[1px] text-[10px] rounded-sm opacity-70" title="Alternative Name">ALT</span>
            <span v-if="staff.isNC" class="bg-ocean-500 text-white px-[4px] py-[1px] text-[10px] rounded-sm opacity-70" title="Not Credited">NC</span>
        </span>

        <a v-if="hasStaffUrl" :href="staffUrl" target="_blank" class="absolute top-1 left-1 text-gray-300 text-2xl cursor-pointer -translate-x-10 group-hover:translate-x-0 group-active:translate-x-0 hover:text-ocean-400 active:text-ocean-400 duration-[40ms] transition-transform">
            <font-awesome-icon icon="fa-solid fa-at" />
        </a>

        <!-- Teleported to body so `position: fixed` stays viewport-relative -->
        <!-- (the row's `transform` fix for Safari also makes it a containing block for fixed descendants) -->
        <Teleport to="body">
            <div
                v-if="(staff.comment || staff.pn) && isCommentVisible"
                class="md-viewer-comment pointer-events-auto select-text text-center fixed bg-ocean-400 text-white font-normal text-sm p-2 rounded-md shadow-sm w-[250px] break-words z-40"
                :style="{ top: `${commentTop}px`, left: `${commentLeft}px` }"
                v-html="commentComputed"
                @mouseenter="isCommentVisible = true"
                @mouseleave="isCommentVisible = false"
            ></div>
        </Teleport>
    </tr>
</template>
